import { useEffect, useState, useRef } from "react";

interface LoadingScreenProps {
  onComplete: () => void;
}

const LOADING_TEXTS = [
  "正在唤醒山海之城...",
  "加载鹭岛风光...",
  "准备闽南美食...",
  "探索城市活力...",
  "即将开启旅程...",
];

export default function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [textIndex, setTextIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const textIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Show content with slight delay for mount animation
    const showTimer = setTimeout(() => setShowContent(true), 100);

    // Progress animation
    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 100;
        }
        // Accelerate near end
        const increment = prev < 70 ? Math.random() * 3 + 1 : Math.random() * 5 + 2;
        return Math.min(prev + increment, 100);
      });
    }, 80);

    // Text cycling
    textIntervalRef.current = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % LOADING_TEXTS.length);
    }, 900);

    return () => {
      clearTimeout(showTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (textIntervalRef.current) clearInterval(textIntervalRef.current);
    };
  }, []);

  // When progress hits 100, trigger fade out
  useEffect(() => {
    if (progress >= 100) {
      if (textIntervalRef.current) clearInterval(textIntervalRef.current);
      const timer = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => onComplete(), 900);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [progress, onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden transition-opacity duration-900 ${
        fadeOut ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      style={{ transitionDuration: "900ms" }}
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/5b978e07472820a648b2826af16e7ddd.jpg"
          alt="厦门加载背景"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(6,182,212,0.25) 0%, transparent 70%)" }}
        />
      </div>

      {/* Animated wave lines */}
      <div className="absolute bottom-0 left-0 right-0 h-24 md:h-32 overflow-hidden opacity-20">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute bottom-0 left-0 right-0 h-16 rounded-t-full"
            style={{
              background: "rgba(6,182,212,0.4)",
              animation: `wave ${2.5 + i * 0.5}s ease-in-out infinite alternate`,
              animationDelay: `${i * 0.3}s`,
              transform: `translateY(${i * 8}px)`,
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div
        className={`relative z-10 flex flex-col items-center transition-all duration-700 ${
          showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        {/* Logo / Icon area */}
        <div className="mb-6 md:mb-8 flex flex-col items-center">
          <div className="relative w-16 h-16 md:w-24 md:h-24 flex items-center justify-center mb-4 md:mb-6">
            <div className="absolute inset-0 rounded-full border-2 border-cyan-400/40" style={{ animation: "spin 8s linear infinite" }} />
            <div className="absolute inset-2 rounded-full border border-cyan-300/30" style={{ animation: "pulse 2s ease-in-out infinite" }} />
            <div className="absolute inset-0 rounded-full" style={{ border: "2px dashed rgba(6,182,212,0.5)", animation: "spin 4s linear infinite reverse" }} />
            <div className="relative z-10 w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <i className="ri-anchor-line text-lg md:text-2xl text-cyan-300" />
            </div>
          </div>

          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-[0.12em] md:tracking-[0.15em] mb-1.5 md:mb-2"
            style={{ fontFamily: "'Noto Serif SC', serif" }}
          >
            遇见厦门
          </h1>
          <p className="text-white/50 text-xs tracking-[0.25em] md:tracking-[0.3em] font-light">
            DISCOVER · XIAMEN
          </p>
        </div>

        {/* Progress section */}
        <div className="w-60 md:w-72 lg:w-96 flex flex-col items-center gap-3 md:gap-4">
          <div className="w-full h-px bg-white/15 relative overflow-hidden rounded-full">
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-cyan-400 to-teal-300 transition-all duration-200 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-0 h-full w-16 bg-gradient-to-r from-transparent via-white/60 to-transparent"
              style={{ left: `${progress - 8}%`, transition: "left 0.2s ease-out" }}
            />
          </div>

          <div className="w-full flex items-center justify-between">
            <span
              key={textIndex}
              className="text-white/60 text-xs tracking-widest font-light"
              style={{ animation: "fadeInUp 0.4s ease-out" }}
            >
              {LOADING_TEXTS[textIndex]}
            </span>
            <span className="text-cyan-300 text-xs font-mono tabular-nums">
              {Math.floor(progress)}%
            </span>
          </div>
        </div>

        {/* Decorative dots */}
        <div className="flex items-center gap-1.5 md:gap-2 mt-7 md:mt-10">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="rounded-full bg-white/30"
              style={{
                width: i === 3 ? "5px" : "3px",
                height: i === 3 ? "5px" : "3px",
                animation: `pulse ${1 + i * 0.15}s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom tagline */}
      <div
        className={`absolute bottom-8 md:bottom-10 left-0 right-0 flex justify-center transition-all duration-1000 delay-300 ${
          showContent ? "opacity-100" : "opacity-0"
        }`}
      >
        <p className="text-white/30 text-xs tracking-[0.3em] md:tracking-[0.4em] font-light">
          一城山海 · 万般美好
        </p>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes wave {
          0% { transform: translateY(0px) scaleX(1); }
          100% { transform: translateY(-12px) scaleX(1.05); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
